import * as React from "react"

/**
 * Two-panel shell for unauthenticated screens: sign-in, forgot, reset.
 *
 * Left panel is the brand chart — a compass rose over depth rings and rhumb
 * lines, drawn as inline SVG so it stays crisp at any size, themes with the
 * page, and does not cost an asset request on the one screen a user hits
 * before anything is cached. Hidden below lg — on a phone the form is the
 * whole job.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
}): JSX.Element {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-[#141a21] text-white lg:flex lg:flex-col lg:justify-between">
        <ChartBackdrop />

        <div className="relative z-10 p-12">
          <div className="flex items-baseline gap-3">
            <span className="font-id text-[22px] font-medium tracking-[0.32em] text-brand">
              PYXIS
            </span>
          </div>
          <p className="mt-2 font-id text-[11px] uppercase tracking-[0.22em] text-white/50">
            Navigation for the pesticide industry
          </p>
        </div>

        <div className="relative z-10 max-w-lg p-12">
          <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-balance">
            Label review, end to end
          </h2>
          <p className="mt-4 text-body leading-relaxed text-white/70">
            Printed labels compared against their EPA-approved masters, with every finding cited to
            the regulation it rests on and confirmed by a specialist before it reaches you.
          </p>

          <ul className="mt-10 space-y-2.5 border-t border-white/10 pt-6 text-body-sm text-white/60">
            <Point>Compared against the contemporaneous approved master</Point>
            <Point>Every finding cited to its regulatory authority</Point>
            <Point>Specialist-confirmed before it reaches your desk</Point>
          </ul>
        </div>

        <p className="relative z-10 px-12 pb-8 text-caption text-white/35">
          Confidential. Label artwork and findings are client property.
        </p>
      </aside>

      <main className="relative flex flex-col justify-center bg-bg px-6 py-12 sm:px-12">
        <FormSideDecoration />

        <div className="auth-form-card relative z-10 mx-auto w-full max-w-[480px]">
          <div className="mb-8 lg:hidden">
            <span className="font-id text-[20px] font-medium tracking-[0.32em] text-brand">
              PYXIS
            </span>
          </div>

          <div className="rounded-2xl border border-border bg-surface p-9 shadow-e2 sm:p-10">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="h-6 w-[2.5px] rounded-full bg-brand"
              />
              <p className="font-id text-[10.5px] uppercase tracking-[0.24em] text-text-3">
                Sign-in
              </p>
            </div>
            <h1 className="mt-3 text-[1.75rem] font-semibold leading-tight tracking-tight text-text">
              {title}
            </h1>
            <p className="mt-2.5 text-body-sm leading-relaxed text-text-2">{subtitle}</p>

            <div className="mt-8">{children}</div>

            {footer && (
              <div className="mt-8 border-t border-border pt-5 text-body-sm leading-relaxed text-text-3">
                {footer}
              </div>
            )}
          </div>
        </div>

        <p className="relative z-10 mx-auto mt-10 w-full max-w-[480px] text-caption text-text-3 lg:hidden">
          Confidential. Label artwork and findings are client property.
        </p>
      </main>
    </div>
  )
}

function Point({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <li className="flex items-start gap-2.5">
      <span
        className="mt-[7px] size-1 shrink-0 rounded-full bg-brand"
        aria-hidden="true"
      />
      <span>{children}</span>
    </li>
  )
}

function ChartBackdrop(): JSX.Element {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_85%_15%,rgb(200_70_26/0.22),transparent_60%)]" />
      <svg
        className="absolute -bottom-32 -right-40 h-[46rem] w-[46rem] text-white"
        viewBox="0 0 400 400"
        fill="none"
        stroke="currentColor"
      >
        <g opacity="0.10" strokeWidth="0.6">
          {[60, 100, 140, 180].map((r) => (
            <circle key={r} cx="200" cy="200" r={r} />
          ))}
          {Array.from({ length: 16 }, (_, i) => {
            const angle = (i * Math.PI) / 8
            return (
              <line
                key={i}
                x1={200 + Math.cos(angle) * 40}
                y1={200 + Math.sin(angle) * 40}
                x2={200 + Math.cos(angle) * 180}
                y2={200 + Math.sin(angle) * 180}
              />
            )
          })}
        </g>
        <g opacity="0.5">
          <path
            d="M200 130 L210 200 L200 270 L190 200 Z"
            fill="#c8461a"
            stroke="none"
            opacity="0.55"
          />
          <path
            d="M130 200 L200 190 L270 200 L200 210 Z"
            fill="currentColor"
            stroke="none"
            opacity="0.14"
          />
          <circle cx="200" cy="200" r="5" fill="#c8461a" stroke="none" opacity="0.7" />
        </g>
      </svg>
    </div>
  )
}

/**
 * Very soft warm wash on the form side — echoes the brand tint from the
 * left panel without competing with the form itself. Sits behind content.
 */
function FormSideDecoration(): JSX.Element {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-[radial-gradient(closest-side,rgb(200_70_26/0.05),transparent)]" />
      <div className="absolute -bottom-48 -left-40 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(closest-side,rgb(200_70_26/0.04),transparent)]" />
    </div>
  )
}
